import * as fileUpload from '@/helpers/fileUploadHelper.js'
import axios from 'axios'
import store from '../store'

export async function uploadMaterial(
  service,
  file,
  subFolder,
  renameFile,
  meta,
  processUpdateCallback
) {
  try {
    // Upload file to s3
    const { config: fileUploaded } = await fileUpload.uploadSingleFile(
      file,
      subFolder,
      renameFile,
      meta
    )

    processUpdateCallback()

    const folderId = fileUpload.getFileFolderId(fileUploaded.folder)

    // Parse and validate material
    const parsedData = await parseMaterial(service, folderId)

    parsedData.folderId = folderId

    processUpdateCallback()

    return {
      ...parsedData
    }
  } catch (error) {
    throw new Error(error.message)
  }
}

export function getPageFormat(format) {
  const pageFormats = {
    A4: 'A4_FORMAT',
    A4Margin: 'A4_MARGIN_FORMAT',
    A5LyingMargin: 'A5_LYING_MARGIN'
  }

  return pageFormats[format]
}

export function generatePreviewParams(service) {
  const state = store.state[service]

  const options = {
    csvFolder:
      !!state.address.id && !!state.address.numberOfRecipients
        ? `csv/${state.address.id}`
        : '',
    pdfFolder: state.upload.folderId,
    senderData: generatePreviewParamsSenderData(service),
    preface: state.upload.letterOptions.includePreface || false,
    service: store.state.cart.serviceType,
    language: store.state.lang,
    addressType: state.address.addressType || ''
  }

  return options
}

function generatePreviewParamsSenderData(service) {
  let senderData = store.state[service].address.senderInformation

  if (!Object.keys(senderData).length) {
    const savedSenderData = JSON.parse(localStorage.getItem('sender'))

    if (!savedSenderData) {
      return ''
    }

    senderData = savedSenderData
  }

  return JSON.stringify({
    country: senderData.country,
    addressLineOne: senderData.name,
    addressLineTwo: senderData.addressOptional,
    street: senderData.address,
    zipcode: senderData.postalCode,
    city: senderData.city
  })
}

export async function generatePreview(service) {
  try {
    const options = generatePreviewParams(service)

    const exist = await doesPDFExist(options.pdfFolder)

    if (!exist) throw new Error('W2P_GENERATE_PREVIEW_ERROR')

    await fileUpload.generatePreview(options)

    const url = await isPreviewReady(options.pdfFolder)

    return url
  } catch (error) {
    throw new Error(error.message)
  }
}

async function doesPDFExist(folderId) {
  const retryCount = 0

  const exists = async () => {
    const data = await fileUpload.isPdfReady(folderId)

    if (!data.message) {
      if (retryCount < 2) {
        return setTimeout(exists, 2000)
      }
      return false
    }

    return true
  }

  try {
    const res = await exists()

    return res
  } catch (error) {
    throw new Error('W2P_GENERATE_PREVIEW_ERROR')
  }
}

async function isPreviewReady(folderId) {
  const retryCount = 0

  const exists = async () => {
    const data = await fileUpload.isPreviewReady(folderId)

    const message = JSON.parse(data.message)

    if (!message.finished) {
      if (retryCount < 2) {
        return setTimeout(exists, 2000)
      }
      throw new Error('Max retry reached')
    }

    return message.url
  }

  try {
    const res = await exists()

    return res
  } catch (error) {
    throw new Error('W2P_GENERATE_PREVIEW_ERROR')
  }
}

async function parseMaterial(service, folderId) {
  try {
    // TODO: get dimensions, preface, singleSided from the product data in store
    const format = store.state.cart.productDelivery.format
    const preface = false
    const singleSided = true

    const pdfProportions = {
      width: format.width,
      height: format.length,
      name: format.name
    }
    const pdfAdditions = {
      preface,
      singleSided
    }
    const result = await validateMaterial(
      folderId,
      pdfProportions,
      pdfAdditions
    )
    const details = getDetails(service, result)
    const validation = getValidation(service, details)

    if (validation.valid && validation.flatteningRequired) {
      await flattenMaterial(folderId)
    }

    return {
      details,
      result: validation.result,
      valid: validation.valid,
      flatteningResult: validation.flatteningResult
    }
  } catch (error) {
    var msg = error.message
    if (
      !['W2P_PDF_VALIDATION_ERROR', 'W2P_PDF_FLATTENING_FAILED'].includes(msg)
    ) {
      msg = 'W2P_PDF_PARSING_ERROR'
    }

    return {
      details: {},
      result: [
        {
          label: {
            key: msg,
            options: {}
          },
          valid: 2
        }
      ],
      valid: false,
      flatteningResult: []
    }
  }
}

async function validateMaterial(pdfFolder, pdfProportions, pdfAdditions) {
  try {
    const response = await axios.post(
      process.env.VUE_APP_PDF_BASE_URL + '/validate',
      {
        pdfFolder,
        pdfProportions,
        pdfAdditions
      }
    )

    return JSON.parse(response.data.message)
  } catch (error) {
    throw new Error('W2P_PDF_VALIDATION_ERROR')
  }
}

/**
 * Add extra properties depending on existing properties.
 * @param {*} details Details to apply extra properties upon
 */

function getValue(obj, key) {
  return obj && obj[key] ? obj[key] : 0
}

function getDetails(service, result) {
  const productDelivery = store.state.cart.productDelivery
  let details = null

  if (result) {
    details = {
      // sendoutName: value (set and used outside of this scope)
      embeddedFonts: {
        valid: result.embeddedFonts.valid,
        errors: result.embeddedFonts.errorDetails
      },
      numberOfPages: {
        original: result.pageLimit.originalCount,
        current: result.pageLimit.current
      }, // Property is used outside of this scope
      size: result.sizeLimit,
      pageFormat: {
        format:
          getValue(result.resolution.format, 'width') +
          'x' +
          getValue(result.resolution.format, 'height') +
          'mm',
        valid: result.resolution.valid,
        error: result.resolution.error,
        formatName: (result.resolution.format || {}).name,
        fileFormat:
          getValue(result.resolution.fileFormat, 'width') +
          'x' +
          getValue(result.resolution.fileFormat, 'height') +
          'mm'
      },
      images: {
        resolution: result.images.resolution,
        transparency: result.images.transparency
      },
      interactive: result.interactiveObjects,
      objects: result.objects.valid,
      rotation: result.rotation.valid,
      flatteningRequired: result.flatteningRequired
    }
  } else {
    details = store.state[service].material.details
  }

  if (details) {
    Object.defineProperties(details, {
      maxNumberOfPages: {
        get: function() {
          return productDelivery.page_count.max
        },
        configurable: true
      },
      minNumberOfPages: {
        get: function() {
          return productDelivery.page_count.base
        },
        configurable: true
      },
      hasPageLimitExceeded: {
        get: function() {
          return details.numberOfPages > this.maxNumberOfPages
        },
        configurable: true
      },
      numberOfPagesExceeded: {
        get: function() {
          return Math.max(
            0,
            details.numberOfPages.current - this.maxNumberOfPages
          )
        },
        configurable: true
      },
      numberOfPagesUnderLimit: {
        get: function() {
          return details.numberOfPages.current < this.minNumberOfPages
        },
        configurable: true
      }
    })
  }
  return details
}

function getValidation(service, details) {
  // TODO: get values data in store
  const productDelivery = store.state.cart.productDelivery

  const result = []
  const flatteningResult = []

  function pushToFlatteningResult(error) {
    // For feature toggle, remove when feature is in prod
    if (details.flatteningRequired) {
      flatteningResult.push(error)
    }
  }

  const fileSize = {
    label: {
      key: 'W2P_UPLOAD_MATERIAL_INVALID_FILE_SIZE',
      options: {
        size: '50MB'
      }
    },
    valid: details.size.valid ? 1 : 2
  }

  let pageFormat = {}
  let interactiveObjects

  if (!details.pageFormat.error || details.pageFormat.error === 'unsupported') {
    //no error or unsupported has the same handling
    pageFormat = {
      label: {
        key: 'W2P_UPLOAD_MATERIAL_INVALID_PAGE_FORMAT',
        options: {
          formatName: details.pageFormat.formatName,
          pageFormat: getPageFormat(details.pageFormat.formatName)
        }
      },
      valid: details.pageFormat.valid ? 1 : 2
    }
  } else {
    //pages of different format, should the error be something else it will not be handled
    pageFormat = {
      label: {
        key: 'W2P_UPLOAD_MATERIAL_INCONSISTENT_PAGE_FORMAT',
        options: {
          formatName: details.pageFormat.formatName,
          pageFormat: getPageFormat(details.pageFormat.formatName)
        }
      },
      valid: 2
    }
  }

  const amountOfPages = {
    label: {
      key: 'W2P_UPLOAD_MATERIAL_INVALID_PAGE_COUNT',
      options: {
        max: productDelivery.page_count.max
      }
    },
    valid:
      details.numberOfPagesExceeded === 0 && !details.numberOfPagesUnderLimit
        ? 1
        : 2
  }

  result.push(fileSize, pageFormat, amountOfPages)

  const imageResolutions = {
    label: {
      key: 'W2P_UPLOAD_MATERIAL_WARN_IMAGE_RESOLUTION',
      options: {
        resolution: '300 dpi'
      }
    },
    valid: details.images.resolution ? 1 : 3
  }

  if (imageResolutions.valid !== 1) {
    // only display on error
    result.push(imageResolutions)
  }

  if (!details.rotation) {
    const rotation = {
      label: {
        key: 'W2P_UPLOAD_MATERIAL_INVALID_IMAGE_ROTATION',
        options: {}
      },
      valid: 2
    }

    result.push(rotation)
  }

  if (
    details.embeddedFonts.valid !== 1 &&
    details.embeddedFonts.errors &&
    details.embeddedFonts.errors.length
  ) {
    details.embeddedFonts.errors.forEach(function(item) {
      const error = {
        label: {
          key: 'W2P_UPLOAD_MATERIAL_WARN_DISALLOWED_FONTS',
          options: {
            errorType: item.errorType,
            font: item.font
          }
        },
        valid: 3
      }
      pushToFlatteningResult(error)
    })
  }
  if (details.images.transparency) {
    const imageTransparancy = {
      label: {
        key: 'W2P_UPLOAD_MATERIAL_WARN_IMAGE_TRANSPARENT',
        options: {}
      },
      valid: 3
    }

    pushToFlatteningResult(imageTransparancy)
  }

  if (details.interactive.valid === 'flatten') {
    const interactiveObjects = {
      label: {
        key: 'W2P_UPLOAD_MATERIAL_WARN_DISALLOWED_ELEMENTS'
      },
      valid: 3
    }
    pushToFlatteningResult(interactiveObjects)
  }

  if (!details.objects) {
    interactiveObjects = {
      label: {
        key: 'W2P_UPLOAD_MATERIAL_WARN_DISALLOWED_ELEMENTS_EXCEED',
        options: {
          count: '25.000'
        }
      },
      valid: 3
    }
    pushToFlatteningResult(interactiveObjects)
  }

  const valid = result.every(function(element) {
    return element.valid && element.valid !== 2
  })

  return {
    result: result,
    valid: valid,
    flatteningResult,
    flatteningRequired: details.flatteningRequired
  }
}

async function flattenMaterial(pdfFolder, pdfAdditions) {
  try {
    const response = await axios.post(
      process.env.VUE_APP_PDF_BASE_URL + '/flatten',
      {
        pdfFolder,
        pdfAdditions
      }
    )

    return JSON.parse(response.data.message)
  } catch (error) {
    throw new Error('W2P_PDF_FLATTENING_FAILED')
  }
}
